import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { BlockSpinner } from 'components/block-spinner';
import { hasValue, parametersDidUpdate, toggleCssClass } from '../../../general/js/general-helpers';
import { getSmtShotViewConfig } from '../../shot-tracker/js/shot-tracker-helpers';
import { shotTrackerConstants } from '../../shot-tracker/js/shot-tracker-constants';
import { isMobile } from '../../../general/js/utils/screen-size-utils';

// iframe `onLoad` never fires when the request can't complete (network/DNS/CSP/blocked),
// so fall back after this delay to stop the spinner instead of letting it spin forever.
const IFRAME_LOAD_TIMEOUT_MS = 15000;

class LivePlayerRouteToHoleSmt extends React.Component {
    constructor(props) {
        super(props);

        this.state = { isIframeLoaded: false };

        this.shotViewContainerRef = React.createRef();
        this.loadTimeoutId = null;

        this.hideSpinner = this.hideSpinner.bind(this);

        this.clipTagParameters = {};
    }

    componentDidMount() {
        this.init();
    }

    componentDidUpdate(prevProps) {
        // Only re-init when a prop init reacts to changes — skip re-renders driven
        // purely by our own spinner state updates.
        const { liveShotView, clipTagParameters } = this.props;

        if (
            prevProps.liveShotView !== liveShotView ||
            prevProps.clipTagParameters !== clipTagParameters
        ) {
            this.init();
        }
    }

    componentWillUnmount() {
        this.clearLoadTimeout();
    }

    getConfig() {
        return getSmtShotViewConfig(this.props);
    }

    isEnabled() {
        const config = this.getConfig();

        return hasValue(config) && !config.disabled;
    }

    init() {
        if (!this.isEnabled()) {
            return;
        }

        this.showShotViewIfClipTagUpdated();
    }

    showShotViewIfClipTagUpdated() {
        const { clipTagParameters, setIsClipTagLoaded } = this.props;

        if (!parametersDidUpdate(this.clipTagParameters, clipTagParameters)) {
            return;
        }

        this.clipTagParameters = get(this.props, shotTrackerConstants.clipTagParameters, {});

        if (hasValue(this.clipTagParameters)) {
            toggleCssClass(this.shotViewContainerRef.current, 'is-hidden', false);
            setIsClipTagLoaded(true);
            this.beginIframeLoad();
        }
    }

    beginIframeLoad() {
        // A new clip tag reloads the iframe, so show the spinner again until it loads
        // and (re)arm the timeout that stops it if the load stalls.
        if (this.state.isIframeLoaded) {
            this.setState({ isIframeLoaded: false });
        }

        this.clearLoadTimeout();
        this.loadTimeoutId = setTimeout(this.hideSpinner, IFRAME_LOAD_TIMEOUT_MS);
    }

    hideSpinner() {
        // Runs on the iframe's `onLoad` and as the stall fallback — either way, stop spinning.
        this.clearLoadTimeout();
        this.setState({ isIframeLoaded: true });
    }

    clearLoadTimeout() {
        if (this.loadTimeoutId) {
            clearTimeout(this.loadTimeoutId);
            this.loadTimeoutId = null;
        }
    }

    getSmtIframeSrc() {
        const config = this.getConfig();
        const { clipTagParameters, scoringPlayer } = this.props;
        const playerId = get(scoringPlayer, 'id', '');
        const roundNo = get(clipTagParameters, 'round', '');
        const holeNo = get(clipTagParameters, 'hole', '');
        const baseUrl = get(config, 'baseUrl', '');

        if (!hasValue(playerId) || !hasValue(roundNo) || !hasValue(holeNo) || !hasValue(baseUrl)) {
            return '';
        }

        const additionalParams = get(config, 'additionalQueryParams', '');
        const breakpointParam = isMobile() ? 'bp=lb' : 'bp=pl';

        return `${baseUrl}?${breakpointParam}&pid=${playerId}&r=${roundNo}&h=${holeNo}${additionalParams}`;
    }

    getIframeDimensions() {
        const config = this.getConfig();
        const mobile = isMobile();
        const widthKey = mobile ? 'mobileIFrameWidthPercentage' : 'iFrameWidthPercentage';
        const heightKey = mobile ? 'mobileIFrameHeightPercentage' : 'iFrameHeightPercentage';

        return {
            widthPercentage: get(config, widthKey, 100),
            heightPercentage: get(config, heightKey, 100)
        };
    }

    render() {
        const { widthPercentage, heightPercentage } = this.getIframeDimensions();
        const { isIframeLoaded } = this.state;
        const src = this.getSmtIframeSrc();

        return (
            <div className="live-player-smt-shot-view">
                <div
                    ref={this.shotViewContainerRef}
                    className="live-player-smt-shot-view__iframe is-hidden"
                >
                    {!isIframeLoaded && <BlockSpinner />}
                    {this.isEnabled() && hasValue(src) && (
                        <iframe
                            src={src}
                            width={`${widthPercentage}%`}
                            height={`${heightPercentage}%`}
                            onLoad={this.hideSpinner}
                        ></iframe>
                    )}
                </div>
            </div>
        );
    }
}

LivePlayerRouteToHoleSmt.propTypes = {
    liveShotView: PropTypes.object,
    clipTagParameters: PropTypes.object,
    setIsClipTagLoaded: PropTypes.func.isRequired,
    scoringPlayer: PropTypes.object
};

export default LivePlayerRouteToHoleSmt;
